import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Cross2Icon } from "@radix-ui/react-icons";
import * as React from "react";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { type DialogGuard, DialogGuardContext, useDialogGuard } from "@/hooks/use-dialog-guard";
import { useT } from "@/lib/i18n/use-t";
import { cn } from "@/lib/utils";
import { T } from "./dialog.i18n";

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/80  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className,
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

// QL-181. Radix dismisses a dialog on an outside click, on Escape and on the
// ✕, and every dialog in this app took that default — one stray click threw
// away a half-filled form with no warning. `DialogContent` now asks first, but
// only once something has actually been edited, so pickers and read-only
// dialogs still close on a stray click exactly as before.
//
// Dirtiness is declared where it can be — shared controls call `markDirty` off
// their own change handlers (see `use-dialog-guard`), which is exact for mouse
// and keyboard alike. What follows is the floor underneath that, for raw
// inputs, one-off buttons and dialogs that have not adopted the seam: a
// capture-phase listener picks up `input` (text fields, textareas,
// contenteditable editors) and `change` (native selects, file inputs).
//
// Clicks are the hard part. Plenty of edits never touch a field: picking a
// component type in the Add Component wizard, a day in the date picker, an
// add/remove-row button. All of those are bare `<button>`s, so an allowlist of
// value-control roles missed them and the dialog still looked untouched. The
// rule is therefore inverted — any click on something actuatable counts, minus
// the affordances below that navigate rather than edit. That trades the odd
// false positive (one extra confirm after clicking, say, a Preview button) for
// never silently dropping work, which is the bargain this ticket asks for.
const ACTUATOR_SELECTOR = [
  "button",
  "summary",
  "[role='button']",
  "[role='switch']",
  "[role='checkbox']",
  "[role='radio']",
  "[role='option']",
  "[role='menuitemcheckbox']",
  "[role='menuitemradio']",
  "[role='slider']",
  "[role='gridcell']",
].join(",");

// Navigation, not editing: tab strips, anything that expands or collapses
// (accordion headers, Select/Popover triggers), search and filter boxes, and
// the dialog's own ✕ — which has its own guard and must not arm the very
// prompt it is about to raise. Also a radio that is already checked (a
// RadioGroup item, a single-mode ToggleGroup item, a choice card): clicking it
// again changes nothing. This listener runs in the capture phase, before the
// click is handled, so aria-checked is still the state before it (QL-760).
const NON_EDIT_SELECTOR = [
  "[data-dialog-guard-ignore]",
  "[data-dialog-close]",
  "[role='tab']",
  "[aria-expanded]",
  "[role='radio'][aria-checked='true']",
].join(",");

// The listener sits on the document rather than on the content element because
// Radix portals Select and Popover panels out of the dialog subtree — a click
// on a select option never reaches the dialog's own DOM. While a modal dialog
// is open nothing outside it is interactive, so document scope costs nothing.
function useEditedHeuristic(enabled: boolean, contentRef: React.RefObject<HTMLElement | null>) {
  const [edited, setEdited] = React.useState(false);

  React.useEffect(() => {
    // Also the reset: a dialog that is reopened has to start clean, and the
    // same `DialogContent` element is reused across opens.
    if (!enabled) {
      setEdited(false);
      return;
    }

    const markEdited = (event: Event) => {
      // Only a real person editing counts. Radix's Select dispatches a genuine,
      // bubbling `change` from a hidden native <select> whenever its value
      // changes — including when the value simply arrives from the server after
      // the dialog opened — which read as an edit and made untouched edit
      // dialogs prompt on close. `isTrusted` is false for anything script
      // dispatched, which is exactly the distinction wanted here: the real
      // user-driven path through those controls reports itself via `markDirty`.
      if (!event.isTrusted) return;
      const target = event.target;
      if (!(target instanceof Element)) return;
      // Dialogs stack, and this listener is on the document, so a dialog opened
      // on top of this one — a translation editor, a picker, the discard prompt
      // itself — would otherwise report its typing as an edit to the dialog
      // underneath. Whichever dialog the event lands in owns it. A portaled
      // Select or Popover panel has no dialog ancestor at all, which is the
      // reason this listener is document-wide, so those still count as ours.
      const layer = target.closest("[role='dialog'],[role='alertdialog']");
      if (layer && layer !== contentRef.current) return;
      if (target.closest(NON_EDIT_SELECTOR)) return;
      if (event.type === "click" && !target.closest(ACTUATOR_SELECTOR)) return;
      setEdited(true);
    };

    document.addEventListener("input", markEdited, true);
    document.addEventListener("change", markEdited, true);
    document.addEventListener("click", markEdited, true);

    return () => {
      document.removeEventListener("input", markEdited, true);
      document.removeEventListener("change", markEdited, true);
      document.removeEventListener("click", markEdited, true);
    };
  }, [enabled, contentRef]);

  return edited;
}

/**
 * Mounts and unmounts with the dialog itself, which is the only thing that can
 * tell the guard when the dialog is actually on screen: `DialogContent`'s own
 * body runs whether the dialog is open or closed, because Radix does the
 * conditional rendering below it inside `Portal`. Left unscoped, the dirty
 * listeners stayed armed the whole time a closed dialog sat in the tree and
 * counted clicks elsewhere on the page as edits — including the very click on
 * the row's edit button that opened it, so an untouched dialog came up already
 * asking to discard.
 */
function DialogOpenSignal({ onOpenChange }: { onOpenChange: (open: boolean) => void }) {
  React.useEffect(() => {
    onOpenChange(true);
    return () => onOpenChange(false);
  }, [onOpenChange]);
  return null;
}

type DialogContentProps = React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
  /**
   * Confirm before an outside click or Escape discards unsaved input. On by
   * default; pass `false` for dialogs that persist each change as it is made,
   * where there is nothing to lose and the prompt would only be in the way.
   *
   * It also takes an expression, which is how a dialog that stays open after
   * saving says so: `guardDismiss={newSecret === null}`. Once the save is done
   * the dialog is showing a result, not pending work, and "Discard changes?"
   * would be asking about edits that are already committed. Turning it off
   * clears the dirty state too, so a dialog that re-arms starts clean.
   */
  guardDismiss?: boolean;
  /**
   * Overrides the heuristic above. Pass it when the dialog already tracks its
   * own dirty state, or when the heuristic reads something as an edit that the
   * dialog does not (a filter, a preview toggle).
   */
  isDirty?: boolean;
};

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(
  (
    {
      className,
      children,
      guardDismiss = true,
      isDirty,
      onEscapeKeyDown,
      onInteractOutside,
      ...props
    },
    ref,
  ) => {
    const t = useT(T);
    const closeRef = React.useRef<HTMLButtonElement>(null);
    const contentRef = React.useRef<HTMLDivElement | null>(null);
    const setContentRef = React.useCallback(
      (node: HTMLDivElement | null) => {
        contentRef.current = node;
        if (typeof ref === "function") ref(node);
        else if (ref) ref.current = node;
      },
      [ref],
    );
    const [confirmingDiscard, setConfirmingDiscard] = React.useState(false);
    const [declaredDirty, setDeclaredDirty] = React.useState(false);
    const [contentOpen, setContentOpen] = React.useState(false);
    const heuristicDirty = useEditedHeuristic(
      guardDismiss && isDirty === undefined && contentOpen,
      contentRef,
    );

    // A dialog that has finished saving turns the guard off (see `guardDismiss`).
    // Whatever it collected on the way there is committed, not pending, so the
    // declared flag has to go with it — otherwise a dialog that re-arms later
    // starts out dirty on the strength of edits that were already saved.
    React.useEffect(() => {
      if (!guardDismiss) setDeclaredDirty(false);
    }, [guardDismiss]);

    const handleContentOpenChange = React.useCallback((open: boolean) => {
      setContentOpen(open);
      if (!open) {
        setDeclaredDirty(false);
        setConfirmingDiscard(false);
      }
    }, []);
    const shouldGuard = guardDismiss && (isDirty ?? (declaredDirty || heuristicDirty));

    const guard = (event: { defaultPrevented: boolean; preventDefault: () => void }) => {
      if (event.defaultPrevented || !shouldGuard) return;
      event.preventDefault();
      setConfirmingDiscard(true);
    };

    // `shouldGuard` is read at call time, so the object may be memoised on the
    // callbacks alone without going stale.
    const shouldGuardRef = React.useRef(shouldGuard);
    shouldGuardRef.current = shouldGuard;
    const dialogGuard = React.useMemo<DialogGuard>(
      () => ({
        markDirty: () => setDeclaredDirty(true),
        setDirty: setDeclaredDirty,
        requestClose: () => {
          if (shouldGuardRef.current) setConfirmingDiscard(true);
          else closeRef.current?.click();
        },
      }),
      [],
    );

    return (
      <DialogPortal>
        <DialogOverlay />
        <DialogPrimitive.Content
          ref={setContentRef}
          className={cn(
            "fixed left-[50%] top-[50%] z-50 grid max-h-[90vh] w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 overflow-x-hidden overflow-y-auto border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 sm:rounded-lg [&>*]:min-w-0",
            className,
          )}
          onEscapeKeyDown={(event) => {
            onEscapeKeyDown?.(event);
            guard(event);
          }}
          onInteractOutside={(event) => {
            onInteractOutside?.(event);
            guard(event);
          }}
          {...props}
        >
          <DialogOpenSignal onOpenChange={handleContentOpenChange} />
          <DialogGuardContext.Provider value={dialogGuard}>{children}</DialogGuardContext.Provider>
          {/* Radix's `composeEventHandlers` skips its own close when the
              consumer's onClick prevents default, which is how the ✕ ends up
              behind the same prompt as Escape and the overlay. */}
          <DialogPrimitive.Close
            data-dialog-close
            onClick={guard}
            className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground"
          >
            <Cross2Icon className="h-4 w-4" />
            <span className="sr-only">{t.close}</span>
          </DialogPrimitive.Close>
          {/* Radix owns `open`, and the guard has no handle on it. This hidden
              Close is how a confirmed discard hands the dialog back its own
              close, whatever the consumer wired to `onOpenChange`. */}
          <DialogPrimitive.Close
            data-dialog-close
            ref={closeRef}
            className="hidden"
            tabIndex={-1}
          />
          <ConfirmDialog
            open={confirmingDiscard}
            onOpenChange={setConfirmingDiscard}
            title={t.discardTitle}
            description={t.discardDescription}
            confirmText={t.discard}
            cancelText={t.keepEditing}
            variant="destructive"
            onConfirm={() => closeRef.current?.click()}
          />
        </DialogPrimitive.Content>
      </DialogPortal>
    );
  },
);
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("flex flex-col space-y-1.5 text-center sm:text-left", className)} {...props} />
);
DialogHeader.displayName = "DialogHeader";

const DialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className)}
    {...props}
  />
);
DialogFooter.displayName = "DialogFooter";

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold leading-none tracking-tight", className)}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

// Pinned-scroll dialog layout (DESIGN.md › Components › Dialogs › Long forms):
// the content is a non-scrolling flex column; only DialogBody scrolls, so the
// close ✕, header and footer stay in reach. Compose as
//   <DialogScrollContent>
//     <DialogScrollHeader>…title/description…</DialogScrollHeader>
//     <form className="flex flex-1 flex-col min-h-0">   ← only when a form wraps body+footer
//       <DialogBody>…fields…</DialogBody>
//       <DialogScrollFooter>…actions…</DialogScrollFooter>
//     </form>
//   </DialogScrollContent>
const DialogScrollContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogContent>
>(({ className, ...props }, ref) => (
  <DialogContent
    ref={ref}
    className={cn("flex max-h-[90vh] flex-col gap-0 overflow-hidden p-0", className)}
    {...props}
  />
));
DialogScrollContent.displayName = "DialogScrollContent";

const DialogScrollHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <DialogHeader className={cn("border-b px-6 pt-6 pb-4", className)} {...props} />
);
DialogScrollHeader.displayName = "DialogScrollHeader";

const DialogBody = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("flex-1 overflow-y-auto px-6 py-4", className)} {...props} />
);
DialogBody.displayName = "DialogBody";

// QL-181. Cancel and the ✕ are the same gesture — "close this, I am done" —
// so they go through the same guard. Every dialog wrote its own Cancel as a
// bare Button, which is why one asked before discarding and the other did not.
// Swap `<Button variant="outline" onClick={() => onClose(false)}>Cancel</Button>`
// for `<DialogCancel>Cancel</DialogCancel>` and drop the handler entirely.
//
// Resetting state does not belong here. It belongs on the dialog's own close
// path — `onOpenChange` — which Escape, the ✕ and the overlay all run and a
// Cancel handler does not; several dialogs cleared a typed password on Cancel
// and left it in state on Escape. `onClick` is for something a Cancel does
// *besides* closing, and it runs first: call `preventDefault()` on the event
// to keep the dialog open.
const DialogCancel = React.forwardRef<
  React.ElementRef<typeof Button>,
  React.ComponentPropsWithoutRef<typeof Button>
>(({ variant = "outline", onClick, ...props }, ref) => {
  const { requestClose } = useDialogGuard();

  return (
    <Button
      // Cancel is a way out of the dialog, not a change to it. Without this the
      // heuristic sees its own click land on a button inside the dialog, marks
      // the dialog edited, and `requestClose` — which runs a beat later, after
      // React has flushed that state — raises a discard prompt for an untouched
      // dialog. The ✕ is exempt for the same reason.
      data-dialog-close
      ref={ref}
      type="button"
      variant={variant}
      onClick={(event) => {
        onClick?.(event);
        if (event.defaultPrevented) return;
        requestClose();
      }}
      {...props}
    />
  );
});
DialogCancel.displayName = "DialogCancel";

const DialogScrollFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <DialogFooter className={cn("border-t px-6 py-4", className)} {...props} />
);
DialogScrollFooter.displayName = "DialogScrollFooter";

export {
  Dialog,
  DialogBody,
  DialogCancel,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogScrollContent,
  DialogScrollFooter,
  DialogScrollHeader,
  DialogTitle,
  DialogTrigger,
};
